import { useState } from 'react';
import { X, Clock, Settings2, Bell, Play } from 'lucide-react';
import type { SkillDefinition, InputField, ScheduleType, SkillColor } from './types';
import { SCHEDULE_LABELS } from './types';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { useAuth } from '@/features/auth';

interface Props {
  skill: SkillDefinition;
  onClose: () => void;
  onActivated: () => void;
}

const COLOR_RING: Record<SkillColor, string> = {
  blue: 'ring-blue-400',
  emerald: 'ring-emerald-400',
  amber: 'ring-amber-400',
  rose: 'ring-rose-400',
  slate: 'ring-slate-400',
  violet: 'ring-violet-400',
};

const STEP_LABELS = ['הגדרת פרמטרים', 'תזמון', 'התראות'];

export function SkillActivationWizard({ skill, onClose, onActivated }: Props) {
  const { activeCompanyId } = useCompany();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: config values
  const [config, setConfig] = useState<Record<string, unknown>>(() => {
    const defaults: Record<string, unknown> = {};
    for (const field of skill.input_schema) {
      if (field.default !== undefined) defaults[field.key] = field.default;
    }
    return defaults;
  });

  // Step 2: schedule
  const [scheduleType, setScheduleType] = useState<ScheduleType>('manual');
  const [scheduleHour, setScheduleHour] = useState(8);
  const [scheduleDay, setScheduleDay] = useState<number | null>(null);
  const [nameOverride, setNameOverride] = useState('');

  // Step 3: notifications
  const [notifyComplete, setNotifyComplete] = useState(true);
  const [notifyError, setNotifyError] = useState(true);

  const handleSave = async () => {
    if (!activeCompanyId || !user) return;
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('company_skills')
        .upsert({
          company_id: activeCompanyId,
          skill_id: skill.id,
          user_id: user.id,
          name_override: nameOverride.trim() || null,
          config,
          schedule_type: scheduleType,
          schedule_day: scheduleDay,
          schedule_hour: scheduleHour,
          notify_on_complete: notifyComplete,
          notify_on_error: notifyError,
          is_active: true,
        }, { onConflict: 'company_id,skill_id' });

      if (err) throw err;
      onActivated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  const colorRing = COLOR_RING[skill.color as SkillColor] ?? 'ring-blue-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl ring-2 ${colorRing} bg-white flex items-center justify-center shrink-0`}>
            <Settings2 className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900">{skill.name_he}</h2>
            <p className="text-sm text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{skill.description_he}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="px-6 py-4 flex items-center gap-2">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 transition-colors
                ${i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                {i < step ? '✓' : i + 1}
              </div>
              <span className={`text-xs font-medium flex-1 ${i === step ? 'text-blue-600' : i < step ? 'text-emerald-600' : 'text-gray-400'}`}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && <div className={`h-px flex-1 ${i < step ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[220px]">

          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">שם מותאם אישית (אופציונלי)</label>
                <input
                  type="text"
                  value={nameOverride}
                  onChange={e => setNameOverride(e.target.value)}
                  placeholder={skill.name_he}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {skill.input_schema.map(field => (
                <ConfigField key={field.key} field={field} value={config[field.key]} onChange={v => setConfig(prev => ({ ...prev, [field.key]: v }))} />
              ))}
              {skill.input_schema.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">הסקיל הזה לא דורש הגדרות נוספות</p>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-2">תדירות הרצה</label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(SCHEDULE_LABELS) as ScheduleType[]).map(type => (
                    <button key={type} onClick={() => setScheduleType(type)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${scheduleType === type ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {SCHEDULE_LABELS[type]}
                    </button>
                  ))}
                </div>
              </div>
              {scheduleType !== 'manual' && (
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">שעת הרצה</label>
                    <select value={scheduleHour} onChange={e => setScheduleHour(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  {scheduleType === 'weekly' && (
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">יום בשבוע</label>
                      <select value={scheduleDay ?? 0} onChange={e => setScheduleDay(Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {['ראשון','שני','שלישי','רביעי','חמישי','שישי','שבת'].map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {scheduleType === 'monthly' && (
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">יום בחודש</label>
                      <select value={scheduleDay ?? 1} onChange={e => setScheduleDay(Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {Array.from({ length: 28 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              {scheduleType === 'manual' && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-sm text-gray-600">
                  <Play className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>הסקיל יופעל ידנית בלחיצת כפתור</span>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">התראה בסיום מוצלח</p>
                    <p className="text-xs text-gray-500">קבל התראה כשהסקיל מסתיים בהצלחה</p>
                  </div>
                </div>
                <button onClick={() => setNotifyComplete(!notifyComplete)}
                  className={`w-11 h-6 rounded-full transition-colors relative ${notifyComplete ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${notifyComplete ? 'right-0.5' : 'left-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">התראה על שגיאה</p>
                    <p className="text-xs text-gray-500">קבל התראה אם הסקיל נכשל</p>
                  </div>
                </div>
                <button onClick={() => setNotifyError(!notifyError)}
                  className={`w-11 h-6 rounded-full transition-colors relative ${notifyError ? 'bg-red-500' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${notifyError ? 'right-0.5' : 'left-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl text-sm text-blue-700">
                <Clock className="w-4 h-4 shrink-0" />
                <span>חיסכון משוער: {skill.estimated_minutes_saved} דקות לכל הרצה</span>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
          <button onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
            {step === 0 ? 'ביטול' : 'הקודם'}
          </button>
          {step < 2 ? (
            <button onClick={() => setStep(s => s + 1)}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors">
              הבא
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
              הפעל סקיל
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfigField({ field, value, onChange }: { field: InputField; value: unknown; onChange: (v: unknown) => void }) {
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
        <label className="text-sm font-medium text-gray-700">{field.label_he}</label>
        <button onClick={() => onChange(!value)}
          className={`w-11 h-6 rounded-full transition-colors relative ${value ? 'bg-blue-500' : 'bg-gray-300'}`}>
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${value ? 'right-0.5' : 'left-0.5'}`} />
        </button>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {field.label_he}{field.required && <span className="text-red-400 mr-0.5">*</span>}
      </label>
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value as string | number ?? ''}
        onChange={e => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
